import React from 'react';
import { Button } from 'react-bootstrap';
import { useMutation } from '@apollo/client';
import PropdocsModal from '../../../components/modal/PropdocsModal';
import { TitleFormattedMessage } from '../../../components/titleFormattedMessage';
import { mutations, queriesV2 } from '../../../api';
import { useNotifications } from '../../../notifications';

interface ShareToWorkspacesModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateName: string;
  teamId: string;
  isShared: boolean;
  onSuccess: () => void;
  type?: 'template' | 'clause';
}

const ShareToWorkspacesModal: React.FC<ShareToWorkspacesModalProps> = ({
  isOpen,
  onClose,
  templateId,
  templateName,
  teamId,
  isShared,
  onSuccess,
  type = 'template',
}) => {
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [shareTemplateToWorkspaces, { loading }] = useMutation(mutations.shareTemplateToWorkspaces);

  const itemType = type === 'clause' ? 'Clause' : 'Template';
  const itemTypeLower = type === 'clause' ? 'clause' : 'template';

  const handleShare = async () => {
    try {
      const result = await shareTemplateToWorkspaces({
        variables: { templateId },
        refetchQueries: [
          {
            query: queriesV2.getDocumentTemplates,
            variables: {
              teamId,
              templateType: type === 'clause' ? 'clause' : 'agreement',
            },
          },
        ],
      });

      const { isShared: newShareStatus, affiliatedWorkspaceCount } = result.data.shareTemplateToWorkspaces;

      if (newShareStatus) {
        notifySuccess({
          message:
            affiliatedWorkspaceCount > 0
              ? `${itemType} shared to ${affiliatedWorkspaceCount} affiliated workspace${affiliatedWorkspaceCount > 1 ? 's' : ''}`
              : `${itemType} sharing enabled (no affiliated workspaces found)`,
        });
      } else {
        notifySuccess({ message: `${itemType} unshared from affiliated workspaces` });
      }

      onSuccess();
      onClose();
    } catch (error) {
      notifyError({ message: `Failed to update ${itemTypeLower} sharing` });
    }
  };

  return (
    <PropdocsModal
      show={isOpen}
      setShow={onClose}
      size="md"
      title={<span>{isShared ? `Unshare ${itemType}` : `Share ${itemType} to Workspaces`}</span>}
    >
      <>
        <div>
          {isShared ? (
            <p>
              Are you sure you want to unshare "{templateName}" from affiliated workspaces? This {itemTypeLower} will no
              longer be accessible in other workspaces owned by the same admin.
            </p>
          ) : (
            <>
              <p>Share "{templateName}" with all workspaces that have the same owner as this workspace?</p>
              <p>
                This {itemTypeLower} will be available as read-only in affiliated workspaces. Users can{' '}
                {type === 'clause' ? 'use it in documents' : 'create documents from it'}, but can only edit it from this
                workspace.
              </p>
            </>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', padding: '1rem 0 1rem 0' }}>
          <Button variant="link" className="text-secondary" onClick={onClose}>
            <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
          </Button>
          <Button variant="primary" onClick={handleShare} disabled={loading}>
            <TitleFormattedMessage
              id={isShared ? 'cta.unshare' : 'cta.share'}
              defaultMessage={isShared ? 'Unshare' : 'Share'}
            />
          </Button>
        </div>
      </>
    </PropdocsModal>
  );
};

export default ShareToWorkspacesModal;
